import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSeo } from '../../hooks/useSeo';
import Navigation from '../feature/Navigation';
import Footer from '../feature/Footer';
import { BrandRule } from '../brand/BrandRule';

export interface LegalSubsection {
  id: string;
  title: string;
  content: string[];
}

export interface LegalSection {
  id: string;
  title: string;
  content: string[];
  subsections?: LegalSubsection[];
}

interface LegalPageLayoutProps {
  namespace: 'terms' | 'privacy';
}

export default function LegalPageLayout({ namespace }: LegalPageLayoutProps) {
  const { t, i18n } = useTranslation(namespace);
  const isRTL = i18n.dir() === 'rtl';
  const textAlignClass = isRTL ? 'text-right' : 'text-left';

  const [activeSection, setActiveSection] = useState<string>('');
  const [tocOpen, setTocOpen] = useState(false);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  const meta = useMemo(() => {
    const raw = t(`${namespace}.meta`, { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as {
        badge?: string;
        title?: string;
        lastUpdated?: string;
        effectiveDate?: string;
        icon?: string;
      };
    }
    return {};
  }, [t, i18n.language, namespace]);

  // Per-page title from the same localized meta the visible <h1> uses.
  useSeo({ title: meta.title ? `${meta.title} | ${i18n.language?.startsWith('ar') ? 'صفقات 24' : 'Safaqat 24'}` : undefined });

  const sections = useMemo(() => {
    const raw = t(`${namespace}.sections`, { returnObjects: true });
    if (Array.isArray(raw)) return raw as LegalSection[];
    return [];
  }, [t, i18n.language, namespace]);

  const tocTitle = useMemo(() => {
    const raw = t(`${namespace}.toc.title`, { returnObjects: true });
    return typeof raw === 'string' ? raw : 'Table of Contents';
  }, [t, i18n.language, namespace]);

  // Scrollspy with IntersectionObserver
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1,
    });

    sectionRefs.current.forEach((el) => {
      observer.observe(el);
    });
    observers.push(observer);

    return () => observers.forEach((obs) => obs.disconnect());
  }, [sections]);

  // Deep linking on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
      setTocOpen(false);
    }
  }, []);

  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      sectionRefs.current.set(id, el);
    }
  }, []);

  return (
    <div
      className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navigation />

      {/* Header: white, the title and the date (owner, 2026-10-07: no tinted band,
          no glow blobs and no badge pill above the title; all three read as an
          AI-made page). */}
      <header className="border-b border-[var(--pv-border-subtle)]">
        <div className={`mx-auto max-w-4xl px-4 pb-8 pt-12 sm:px-6 sm:pt-16 ${textAlignClass}`}>
          <h1 className="text-3xl font-bold leading-tight text-[var(--pv-color-primary)] sm:text-4xl">
            {meta.title || ''}
          </h1>
          <BrandRule className="mt-3" />
          {meta.lastUpdated && (
            <p className="mt-3 text-[0.9375rem] text-[var(--pv-color-text-muted)]">{meta.lastUpdated}</p>
          )}
        </div>
      </header>

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        {/* Source order is TOC then content; `dir` handles the side — in RTL the
            grid's first track is on the right, so the TOC sits right and the
            content fills the left (and the mirror in LTR). No manual col-start
            overrides, which previously fought `dir` and pushed the Arabic TOC
            to the wrong (left) side. */}
        <div className="lg:grid lg:grid-cols-4 lg:gap-10">
          {/* Sidebar TOC - Desktop */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24">
              <h2 className={`mb-3 text-[0.9375rem] font-semibold text-[var(--pv-color-primary)] ${textAlignClass}`}>
                {tocTitle}
              </h2>
              <ul className="space-y-0.5">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      aria-current={activeSection === section.id ? 'true' : undefined}
                      className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-[0.875rem] transition-colors ${textAlignClass} ${
                        activeSection === section.id
                          ? 'bg-[var(--pv-color-surface-sunken)] font-semibold text-[var(--pv-color-primary)]'
                          : 'text-[var(--pv-color-text-muted)] hover:bg-[var(--pv-color-surface-soft)] hover:text-[var(--pv-color-text)]'
                      }`}
                    >
                      <span className="shrink-0 tabular-nums">{index + 1}.</span>
                      <span>{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Mobile TOC */}
          <div className="mb-8 lg:hidden">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              aria-expanded={tocOpen}
              className={`flex w-full items-center justify-between rounded-xl border border-[var(--pv-border-soft)] px-4 py-3 text-[0.9375rem] font-semibold text-[var(--pv-color-text)] ${textAlignClass}`}
            >
              {tocTitle}
              <i className={`ri-arrow-${tocOpen ? 'up' : 'down'}-s-line text-[1.25rem] leading-none text-[var(--pv-color-text-muted)]`} aria-hidden="true" />
            </button>
            {tocOpen && (
              <ul className="mt-2 space-y-0.5 border-b border-[var(--pv-border-subtle)] pb-3">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-[0.875rem] text-[var(--pv-color-text-muted)] transition-colors hover:bg-[var(--pv-color-surface-soft)] hover:text-[var(--pv-color-text)] ${textAlignClass}`}
                    >
                      <span className="shrink-0 tabular-nums">{index + 1}.</span>
                      <span>{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Content: one document, sections split by hairlines (no box, no number
              tile, no side stripe per subsection). Legal sections stay numbered. */}
          <div className="lg:col-span-3">
            {sections.map((section, index) => (
              <article
                key={section.id}
                id={section.id}
                ref={(el) => registerRef(section.id, el)}
                className="scroll-mt-24 border-t border-[var(--pv-border-subtle)] py-8 first:border-t-0 first:pt-0"
              >
                <h2 className={`text-xl font-bold text-[var(--pv-color-primary)] sm:text-2xl ${textAlignClass}`}>
                  <span className="tabular-nums">{index + 1}.</span> {section.title}
                </h2>

                <div className={`mt-4 space-y-4 ${textAlignClass}`}>
                  {section.content.map((paragraph, pIdx) => (
                    <p key={pIdx} className="text-base leading-relaxed text-[var(--pv-color-text)]">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {section.subsections && section.subsections.length > 0 && (
                  <div className="mt-6 space-y-6">
                    {section.subsections.map((sub) => (
                      <div key={sub.id} id={sub.id}>
                        <h3 className={`text-lg font-semibold text-[var(--pv-color-text)] ${textAlignClass}`}>
                          {sub.title}
                        </h3>
                        <div className={`mt-2 space-y-3 ${textAlignClass}`}>
                          {sub.content.map((paragraph, pIdx) => (
                            <p key={pIdx} className="text-base leading-relaxed text-[var(--pv-color-text)]">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}

            {/* Questions: a closing line with the two ways to reach us. */}
            <div className={`border-t border-[var(--pv-border-subtle)] pt-8 print:hidden ${textAlignClass}`}>
              <h2 className="text-lg font-semibold text-[var(--pv-color-text)]">
                {t(`${namespace}.contact.title`, { defaultValue: isRTL ? 'هل لديك أسئلة؟' : 'Have Questions?' })}
              </h2>
              <p className="mt-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {t(`${namespace}.contact.description`, {
                  defaultValue: isRTL
                    ? 'فريقنا جاهز للمساعدة. تواصل معنا في أي وقت.'
                    : 'Our team is ready to help. Reach out anytime.',
                })}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                <a
                  href="mailto:info@safaqat24.sa"
                  className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[var(--pv-color-primary)] underline-offset-4 hover:underline"
                >
                  <i className="ri-mail-line" aria-hidden="true" />
                  info@safaqat24.sa
                </a>
                <a
                  href="tel:+966505608271"
                  dir="ltr"
                  className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[var(--pv-color-primary)] underline-offset-4 hover:underline"
                >
                  <i className="ri-phone-line" aria-hidden="true" />
                  +966 50 560 8271
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
